"use client"

import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import React, { ReactNode, useEffect, useState } from "react"
import SwiperCore from "swiper"
import "swiper/css"
import { A11y, Navigation, Pagination, Scrollbar } from "swiper/modules"
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react"
import { iconsMap } from "../../public/icons/iconsMap"
import { useIsDesktop } from "../common/bit/MediaQueries"
import { sizes } from "../theme/theme"

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y])

type SwiperOnMobileProps = {
  children: ReactNode[]
  swiperProps?: SwiperProps
  desktopItemsGap?: string
  desktopCardHeight?: string
  className?: string
  arrowIconName?: string
}

const SwiperWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "desktopItemsGap" && prop !== "desktopCardHeight",
})<{ desktopItemsGap?: string; desktopCardHeight?: string }>`
  display: flex;
  flex-flow: row wrap;
  align-items: stretch;
  justify-content: center;
  width: 100%;
  gap: ${(p) => p.desktopItemsGap || sizes.baseTriple};
  max-height: ${(prop) =>
    prop.desktopCardHeight
      ? prop.desktopCardHeight
      : "unset"}; // the overflow hidden and height is to make overflow cards desapear according to design
  overflow: ${(prop) =>
    prop.desktopCardHeight
      ? "hidden"
      : "visible"}; // the overflow hidden and height is to make overflow cards desapear according to design
  .swiper {
    overflow: visible;
    width: 90%;
    padding: 0;
  }
  .swiper-button-next,
  .swiper-button-prev {
    display: none !important;
  }
`
const StyledSwiperSlide = styled(SwiperSlide)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 auto;
  width: 100%;
`
const ButtonWrapper = styled(Box)`
  display: flex;
  width: 100%;
  justify-content: center;
  .button-prev,
  .button-next {
    padding: ${sizes.baseSingle};
  }
  .button-prev {
    transform: rotate(180deg);
  }
`

const SwiperOnMobile = ({
  children,
  swiperProps = {
    navigation: true,
  },
  desktopItemsGap,
  className,
  desktopCardHeight,
  arrowIconName,
}: SwiperOnMobileProps) => {
  const [mounted, setMounted] = useState(false)
  const isDesktop = useIsDesktop()
  const ArrowRightIcon =
    iconsMap[arrowIconName || "arrow_lightOrange_black_line"]

  useEffect(() => {
    setMounted(true)
  }, [])

  // Don't render until mounted to avoid hydration mismatch
  if (!mounted) {
    return (
      <SwiperWrapper
        desktopItemsGap={desktopItemsGap}
        desktopCardHeight={desktopCardHeight}
        className={className}
      >
        {children}
      </SwiperWrapper>
    )
  }

  return (
    <SwiperWrapper
      desktopItemsGap={desktopItemsGap}
      desktopCardHeight={desktopCardHeight}
      className={className}
    >
      {children &&
      !isDesktop &&
      Array.isArray(children) &&
      children.length > 1 ? (
        <>
          <Swiper
            navigation={
              swiperProps.navigation
                ? {
                    nextEl: ".button-next",
                    prevEl: ".button-prev",
                  }
                : false
            }
            spaceBetween={
              swiperProps.spaceBetween ? swiperProps.spaceBetween : 20
            }
            modules={[Navigation]}
            autoHeight={swiperProps.autoHeight ? swiperProps.autoHeight : true}
            slidesPerGroup={
              swiperProps.slidesPerGroup ? swiperProps.slidesPerGroup : 1
            }
            loop={swiperProps.loop || swiperProps.navigation ? true : false}
            centeredSlides={swiperProps.centeredSlides}
            slidesPerView={
              swiperProps.slidesPerView ? swiperProps.slidesPerView : 1.75
            }
            freeMode={swiperProps.freeMode}
          >
            {children.map((child, index) => {
              if (React.isValidElement(child)) {
                return (
                  <StyledSwiperSlide
                    key={`swiper-mobile-${index.toString()}-${child.key}`}
                  >
                    {child}
                  </StyledSwiperSlide>
                )
              }
            })}
          </Swiper>
          {swiperProps.navigation && (
            <ButtonWrapper>
              <Button className="button-prev" variant="ghost">
                <ArrowRightIcon />
              </Button>
              <Button className="button-next" variant="ghost">
                <ArrowRightIcon />
              </Button>
            </ButtonWrapper>
          )}
        </>
      ) : (
        children
      )}
    </SwiperWrapper>
  )
}

export default SwiperOnMobile
